import { useEffect, useState } from 'react'
import {
  AlertTriangle,
  Box,
  Copy,
  ClipboardPaste,
  Layers,
  Minus,
  MousePointer2,
  Plus,
  Radio,
  Redo2,
  Sparkles,
  Square,
  Trash2,
  Undo2,
  Users,
  Wifi,
} from 'lucide-react'
import { Button, Input, Select } from '../ui'
import {
  INFRA_DEVICE_TYPES,
  OBSTACLE_TYPES,
  WALL_MATERIALS,
} from '../../lib/floorPlan/materials'
import type {
  ClientSpeedTestResult,
  EditorTool,
  FloorAlarmDot,
  FloorClientDot,
  FloorDevice,
  FloorGeometry,
  FloorLayerKey,
  FloorObstacle,
  FloorRecommendation,
  FloorRoom,
  FloorWall,
  PlacedAp,
  WallMaterial,
  ApMountType,
} from '../../lib/floorPlan/types'
import { AP_MOUNT_PRESETS, defaultMountHeight } from '../../lib/floorPlan/apMounts'
import { formatChannelLabel } from '../../lib/floorPlan/rfChannels'
import type { FloorPlanMetrics, FloorPlanOptimizationResult } from '../../lib/floorPlan/rfOptimizer'
import type { RequirementPassSummary } from '../../lib/floorPlan/rfFloorMetrics'
import type { InterferencePlanStatus } from '../../lib/floorPlan/rfInterferencePolicy'
import { formatObstacleSize, resolveObstacleFootprint } from '../../lib/floorPlan/obstacles'
import {
  DEFAULT_WALL_HEIGHT_M,
  DEFAULT_WALL_THICKNESS_M,
  formatWallSize,
  resolveWallDimensions,
  type WallDims,
} from '../../lib/floorPlan/walls'
import FloorPlanApPanel from './FloorPlanApPanel'
import FloorPlanClientPanel from './FloorPlanClientPanel'
import FloorPlanPalette from './FloorPlanPalette'
import FloorPlanRfPanel from './FloorPlanRfPanel'

export type ObstacleDims = { widthM: number; depthM: number; heightM: number }

export type SidebarTab = 'view' | 'draw' | 'aps' | 'rf' | 'inspect'

const LAYER_GROUPS: { title: string; keys: FloorLayerKey[] }[] = [
  {
    title: 'Map',
    keys: ['layout', 'rooms', 'walls'],
  },
  {
    title: 'Overlays',
    keys: ['devices', 'clients', 'alarms', 'cameras', 'sensors', 'topology'],
  },
]

const TOOLS: { id: EditorTool; label: string; icon: typeof MousePointer2; hint: string }[] = [
  { id: 'select', label: 'Select', icon: MousePointer2, hint: 'Click to select · drag to move · Delete to remove' },
  { id: 'wall', label: 'Wall', icon: Minus, hint: 'Click-drag to draw a wall segment' },
  { id: 'room', label: 'Room', icon: Square, hint: 'Click-drag a room rectangle' },
  { id: 'obstacle', label: 'Obstacle', icon: Box, hint: 'Click to place RF obstacle' },
  { id: 'device', label: 'Device', icon: Radio, hint: 'Click to place infrastructure' },
]

type Props = {
  tab: SidebarTab
  onTabChange: (t: SidebarTab) => void
  layers: FloorGeometry['layers']
  geometry: FloorGeometry
  tool: EditorTool
  wallMaterial: WallMaterial
  wallDims: WallDims
  obstacleType: string
  obstacleDims: ObstacleDims
  floorWidthM: number
  floorHeightM: number
  floorId: string
  savingFloorSize?: boolean
  deviceType: FloorDevice['type']
  canUndo: boolean
  canRedo: boolean
  selectedAp: PlacedAp | null
  connectedClients?: FloorClientDot[]
  selectedClient?: FloorClientDot | null
  onSelectClient?: (clientId: string) => void
  onClientSpeedTestComplete?: (clientId: string, result: ClientSpeedTestResult) => void
  onRunClientSpeedTest?: (clientId: string, rssi: number) => Promise<ClientSpeedTestResult>
  selectedDevice: FloorDevice | null
  selectedWall: FloorWall | null
  selectedRoom: FloorRoom | null
  selectedObstacle: FloorObstacle | null
  selectedAlarm: FloorAlarmDot | null
  showTopology: boolean
  isFloorEmpty: boolean
  addApName: string
  inventoryAps: { id: string; name: string; model?: string }[]
  recommendations: FloorRecommendation[]
  rfMetrics?: FloorPlanMetrics | null
  requirementPass?: RequirementPassSummary | null
  interferencePlan?: InterferencePlanStatus | null
  onUndo: () => void
  onRedo: () => void
  onToolChange: (t: EditorTool) => void
  onLayerToggle: (key: FloorLayerKey, on: boolean) => void
  onGeometryPatch: (patch: Partial<FloorGeometry>) => void
  onWallMaterial: (m: WallMaterial) => void
  onWallDims: (d: WallDims) => void
  onUpdateWallDims: (patch: Partial<WallDims>) => void
  onObstacleType: (t: string) => void
  onObstacleDims: (d: ObstacleDims) => void
  onUpdateObstacleDims: (patch: Partial<ObstacleDims>) => void
  onSaveFloorSize: (widthM: number, heightM: number) => void | Promise<boolean>
  onDeviceType: (t: FloorDevice['type']) => void
  onTxPower: (v: number) => void
  onApMount: (patch: {
    mount?: ApMountType
    mountHeightM?: number
    mountOrientationDeg?: number
  }) => void
  onRemoveAp: () => void
  onDeleteDevice: () => void
  onDeleteWall: () => void
  onDeleteRoom: () => void
  onDeleteObstacle: () => void
  onCopyElement: () => void
  onDuplicateElement: () => void
  onPasteElement: () => void
  geometryClipHint?: string | null
  hasGeometryClipboard?: boolean
  onReplaceWallMaterial: (m: WallMaterial) => void
  onReplaceObstacleType: (t: string) => void
  onRenameRoom: (name: string) => void
  onUpdateRoomCoverage: (coverage: { minRssiDbm: number } | undefined) => void
  onClearMapElements: () => void
  onDismissAlarm: () => void
  onAddAp: () => void
  onAddApName: (name: string) => void
  onApplyRecommendation?: (rec: FloorRecommendation) => void
  applyingRecommendationId?: string | null
  onOptimizeFloor?: () => void
  optimizing?: boolean
  optimizationOverlayOpen?: boolean
  optimizationResult?: FloorPlanOptimizationResult | null
  onDismissOptimization?: () => void
  placedApCount?: number
  placedAps?: { id: string; ap: string }[]
  selectedApId?: string | null
  onSelectAp?: (id: string) => void
}

function GeometryClipActions({
  hint,
  hasClipboard,
  onCopy,
  onDuplicate,
  onPaste,
}: {
  hint?: string | null
  hasClipboard: boolean
  onCopy: () => void
  onDuplicate: () => void
  onPaste: () => void
}) {
  return (
    <div className="space-y-2 border-t border-mg-border pt-2">
      {hint && <p className="text-[11px] font-medium text-mg-accent">{hint}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" type="button" className="flex-1" onClick={onCopy} title="Copy (⌘C)">
          <Copy size={14} /> Copy
        </Button>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          className="flex-1"
          onClick={onDuplicate}
          title="Duplicate (⌘D)"
        >
          <Copy size={14} /> Duplicate
        </Button>
      </div>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        className="w-full"
        disabled={!hasClipboard}
        onClick={onPaste}
        title="Paste (⌘V)"
      >
        <ClipboardPaste size={14} /> Paste copy
      </Button>
    </div>
  )
}

export default function FloorPlanSidebar(props: Props) {
  const {
    tab,
    onTabChange,
    layers,
    geometry,
    tool,
    wallMaterial,
    wallDims,
    obstacleType,
    obstacleDims,
    floorWidthM,
    floorHeightM,
    floorId,
    savingFloorSize,
    deviceType,
    canUndo,
    canRedo,
    selectedAp,
    connectedClients = [] as FloorClientDot[],
    selectedClient = null,
    onSelectClient,
    onClientSpeedTestComplete,
    onRunClientSpeedTest,
    selectedDevice,
    selectedWall,
    selectedRoom,
    selectedObstacle,
    selectedAlarm,
    showTopology,
    isFloorEmpty,
    addApName,
    inventoryAps,
    recommendations,
    rfMetrics,
    requirementPass,
    interferencePlan,
    onUndo,
    onRedo,
    onToolChange,
    onLayerToggle,
    onGeometryPatch,
    onWallMaterial,
    onWallDims,
    onUpdateWallDims,
    onObstacleType,
    onObstacleDims,
    onUpdateObstacleDims,
    onSaveFloorSize,
    onDeviceType,
    onTxPower,
    onApMount,
    onRemoveAp,
    onDeleteDevice,
    onDeleteWall,
    onDeleteRoom,
    onDeleteObstacle,
    onCopyElement,
    onDuplicateElement,
    onPasteElement,
    geometryClipHint,
    hasGeometryClipboard = false,
    onReplaceWallMaterial,
    onReplaceObstacleType,
    onRenameRoom,
    onUpdateRoomCoverage,
    onClearMapElements,
    onDismissAlarm,
    onAddAp,
    onAddApName,
    onApplyRecommendation,
    applyingRecommendationId,
    onOptimizeFloor,
    optimizing,
    optimizationOverlayOpen,
    optimizationResult,
    onDismissOptimization,
    placedApCount = 0,
    placedAps = [],
    selectedApId = null,
    onSelectAp,
  } = props

  const recCount = recommendations.length
  const highRecCount = recommendations.filter(r => r.priority === 'HIGH').length

  const tabs: { id: SidebarTab; label: string; icon: typeof Layers; badge?: number }[] = [
    { id: 'view', label: 'View', icon: Layers },
    { id: 'draw', label: 'Draw', icon: Minus },
    { id: 'aps', label: 'APs', icon: Wifi },
    { id: 'rf', label: 'RF', icon: Sparkles, badge: recCount > 0 ? recCount : undefined },
    { id: 'inspect', label: 'Inspect', icon: AlertTriangle },
  ]

  const [draftFloorW, setDraftFloorW] = useState(String(floorWidthM))
  const [draftFloorH, setDraftFloorH] = useState(String(floorHeightM))
  const [floorSizeHint, setFloorSizeHint] = useState<string | null>(null)

  useEffect(() => {
    setDraftFloorW(String(floorWidthM))
    setDraftFloorH(String(floorHeightM))
  }, [floorWidthM, floorHeightM, floorId])

  function parseFloorDimDraft(raw: string): number | null {
    const trimmed = String(raw).trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return null
    return Math.round(n)
  }

  function floorSizeValidationError(): string | null {
    const w = parseFloorDimDraft(draftFloorW)
    const h = parseFloorDimDraft(draftFloorH)
    if (w == null || h == null) return 'Enter valid numbers for width and height'
    if (w < 5 || w > 500 || h < 5 || h > 500) return 'Each dimension must be between 5 and 500 m'
    return null
  }

  function floorSizeUnchanged(): boolean {
    const w = parseFloorDimDraft(draftFloorW)
    const h = parseFloorDimDraft(draftFloorH)
    if (w == null || h == null) return false
    return w === floorWidthM && h === floorHeightM
  }

  async function applyFloorSize() {
    const err = floorSizeValidationError()
    if (err) {
      setFloorSizeHint(err)
      return
    }
    const w = parseFloorDimDraft(draftFloorW)!
    const h = parseFloorDimDraft(draftFloorH)!
    if (floorSizeUnchanged()) {
      setFloorSizeHint('Already applied — change a value first')
      return
    }
    setFloorSizeHint(null)
    const ok = await onSaveFloorSize(w, h)
    if (ok) {
      setFloorSizeHint(`Applied ${w}×${h} m — aspect ratio and RF scale updated`)
    } else {
      setFloorSizeHint('Save failed — select a site in the header if needed, or check the error above the map')
    }
  }

  return (
    <aside className="floor-sidebar w-full shrink-0 lg:w-[300px] xl:w-[320px]">
      <div className="floor-sidebar-toolbar flex items-center gap-0.5 rounded-t-lg border border-b-0 border-mg-border bg-mg-page px-1.5 py-1.5">
        <Button variant="ghost" size="sm" disabled={!canUndo} onClick={onUndo} title="Undo (⌘Z)">
          <Undo2 size={15} />
        </Button>
        <Button variant="ghost" size="sm" disabled={!canRedo} onClick={onRedo} title="Redo (⇧⌘Z)">
          <Redo2 size={15} />
        </Button>
        <span className="mx-1 h-4 w-px bg-mg-border" />
        {tabs.map(({ id, label, icon: Icon, badge }) => (
          <button
            key={id}
            type="button"
            onClick={() => onTabChange(id)}
            className={`floor-sidebar-tab ${tab === id ? 'floor-sidebar-tab--active' : ''}`}
          >
            <Icon size={13} />
            {label}
            {badge != null && (
              <span
                className={`ml-0.5 rounded-full px-1.5 py-px text-[9px] font-bold tabular-nums ${
                  id === 'rf' && highRecCount > 0
                    ? 'bg-red-500 text-white'
                    : 'bg-mg-accent/15 text-mg-accent'
                }`}
              >
                {badge}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="floor-sidebar-body space-y-3 rounded-b-lg border border-mg-border bg-mg-surface p-2.5">
        {tab === 'view' && (
          <>
            {LAYER_GROUPS.map(group => (
              <div key={group.title} className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-mg-muted">{group.title}</p>
                <div className="flex flex-wrap gap-1.5">
                  {group.keys.map(key => {
                    const labels: Record<FloorLayerKey, string> = {
                      layout: 'Layout',
                      rooms: 'Rooms',
                      walls: 'Walls',
                      devices: 'Devices',
                      clients: 'Clients',
                      heatmap: 'Coverage',
                      alarms: 'Alarms',
                      cameras: 'Cameras',
                      sensors: 'Sensors',
                      topology: 'Topology',
                    }
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onLayerToggle(key, !layers[key])}
                        className={`floor-layer-chip ${layers[key] ? 'floor-layer-chip--on' : ''}`}
                      >
                        {labels[key]}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            {floorId && (
              <div className="space-y-2 border-t border-mg-border pt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-mg-muted">Floor size</p>
                <p className="text-[11px] leading-snug text-mg-muted">
                  Real-world width and height in meters — scales RF distances and obstacle footprints. Walls and APs
                  stay at the same % position on the plan.
                </p>
                <p className="text-[11px] tabular-nums text-mg-text">
                  Applied: {floorWidthM}×{floorHeightM} m
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs text-mg-muted">
                    Width (m)
                    <Input
                      type="number"
                      min={5}
                      max={500}
                      step={1}
                      value={draftFloorW}
                      className="mt-1"
                      onChange={e => {
                        setDraftFloorW(e.target.value)
                        setFloorSizeHint(null)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void applyFloorSize()
                      }}
                    />
                  </label>
                  <label className="block text-xs text-mg-muted">
                    Height (m)
                    <Input
                      type="number"
                      min={5}
                      max={500}
                      step={1}
                      value={draftFloorH}
                      className="mt-1"
                      onChange={e => {
                        setDraftFloorH(e.target.value)
                        setFloorSizeHint(null)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void applyFloorSize()
                      }}
                    />
                  </label>
                </div>
                {floorSizeHint && (
                  <p
                    className={`text-[11px] leading-snug ${
                      floorSizeHint.startsWith('Applied') ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'
                    }`}
                  >
                    {floorSizeHint}
                  </p>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className="w-full"
                  disabled={savingFloorSize}
                  onClick={() => void applyFloorSize()}
                >
                  {savingFloorSize ? 'Saving…' : 'Apply floor size'}
                </Button>
              </div>
            )}

            <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-mg-muted">Site info</p>
              <label className="block text-xs text-mg-muted">
                Building
                <Input
                  value={geometry.metadata?.buildingName ?? ''}
                  className="mt-1"
                  placeholder="Optional"
                  onChange={e =>
                    onGeometryPatch({
                      metadata: { ...geometry.metadata, buildingName: e.target.value.trim() || undefined },
                    })
                  }
                />
              </label>
              <label className="block text-xs text-mg-muted">
                Level
                <Input
                  value={geometry.metadata?.floorLevel ?? ''}
                  className="mt-1"
                  placeholder="Optional"
                  onChange={e =>
                    onGeometryPatch({
                      metadata: { ...geometry.metadata, floorLevel: e.target.value.trim() || undefined },
                    })
                  }
                />
              </label>
            </div>
          </>
        )}

        {tab === 'draw' && (
          <>
            {isFloorEmpty && (
              <div className="rounded-lg border border-dashed border-mg-accent/40 bg-mg-page p-3">
                <p className="mb-2 text-xs font-semibold text-mg-text">Empty floor — drag items onto the map</p>
                <FloorPlanPalette inventoryAps={inventoryAps} compact />
              </div>
            )}

            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-mg-muted">Tools</p>
              <div className="grid grid-cols-2 gap-1.5">
                {TOOLS.map(({ id, label, icon: Icon, hint }) => (
                  <button
                    key={id}
                    type="button"
                    title={hint}
                    onClick={() => onToolChange(id)}
                    className={`floor-tool-btn ${tool === id ? 'floor-tool-btn--active' : ''}`}
                  >
                    <Icon size={15} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-mg-muted">{TOOLS.find(t => t.id === tool)?.hint}</p>
            </div>

            {tool === 'wall' && (
              <>
                <div>
                  <label className="text-xs font-semibold text-mg-muted">Wall material</label>
                  <Select value={wallMaterial} className="mt-1 w-full" onChange={e => onWallMaterial(e.target.value as WallMaterial)}>
                    {WALL_MATERIALS.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.label} · {m.attenuationDb} dB
                      </option>
                    ))}
                  </Select>
                </div>
                <p className="text-[11px] leading-snug text-mg-muted">
                  Set height and thickness before drawing — low glass dividers (~1.2 m) won&apos;t block ceiling APs.
                </p>
                <label className="block text-xs text-mg-muted">
                  Thickness · {wallDims.thicknessM.toFixed(2)} m
                  <input
                    type="range"
                    min={0.08}
                    max={0.5}
                    step={0.02}
                    value={wallDims.thicknessM}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onWallDims({ ...wallDims, thicknessM: Number(e.target.value) })}
                  />
                </label>
                <label className="block text-xs text-mg-muted">
                  Height · {wallDims.heightM.toFixed(1)} m
                  <input
                    type="range"
                    min={0.8}
                    max={4}
                    step={0.1}
                    value={wallDims.heightM}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onWallDims({ ...wallDims, heightM: Number(e.target.value) })}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onWallDims({ heightM: 1.2, thicknessM: 0.12 })}
                  >
                    Low separator
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      onWallDims({ heightM: DEFAULT_WALL_HEIGHT_M, thicknessM: DEFAULT_WALL_THICKNESS_M })
                    }
                  >
                    Full partition
                  </Button>
                </div>
              </>
            )}
            {tool === 'obstacle' && (
              <>
                <div>
                  <label className="text-xs font-semibold text-mg-muted">Obstacle type</label>
                  <Select value={obstacleType} className="mt-1 w-full" onChange={e => onObstacleType(e.target.value)}>
                    {OBSTACLE_TYPES.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.label} · {o.attenuationDb} dB
                      </option>
                    ))}
                  </Select>
                </div>
                <p className="text-[11px] leading-snug text-mg-muted">
                  Set footprint and height before placing — tall racks block low APs; high wall APs may clear them.
                </p>
                <label className="block text-xs text-mg-muted">
                  Width · {obstacleDims.widthM.toFixed(1)} m
                  <input
                    type="range"
                    min={0.4}
                    max={4}
                    step={0.1}
                    value={obstacleDims.widthM}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onObstacleDims({ ...obstacleDims, widthM: Number(e.target.value) })}
                  />
                </label>
                <label className="block text-xs text-mg-muted">
                  Depth · {obstacleDims.depthM.toFixed(1)} m
                  <input
                    type="range"
                    min={0.4}
                    max={4}
                    step={0.1}
                    value={obstacleDims.depthM}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onObstacleDims({ ...obstacleDims, depthM: Number(e.target.value) })}
                  />
                </label>
                <label className="block text-xs text-mg-muted">
                  Height · {obstacleDims.heightM.toFixed(1)} m
                  <input
                    type="range"
                    min={0.8}
                    max={4}
                    step={0.1}
                    value={obstacleDims.heightM}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onObstacleDims({ ...obstacleDims, heightM: Number(e.target.value) })}
                  />
                </label>
              </>
            )}
            {tool === 'device' && (
              <div>
                <label className="text-xs font-semibold text-mg-muted">Device type</label>
                <Select value={deviceType} className="mt-1 w-full" onChange={e => onDeviceType(e.target.value as FloorDevice['type'])}>
                  {INFRA_DEVICE_TYPES.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {!isFloorEmpty && (
              <div className="border-t border-mg-border pt-3">
                <FloorPlanPalette inventoryAps={inventoryAps} compact />
                <Button variant="ghost" size="sm" className="mt-2 w-full text-mg-danger" onClick={onClearMapElements}>
                  <Trash2 size={14} /> Clear all map elements
                </Button>
              </div>
            )}
          </>
        )}

        {tab === 'aps' && (
          <FloorPlanApPanel
            floorId={floorId}
            addApName={addApName}
            placedApCount={placedApCount}
            inventoryAps={inventoryAps}
            placedAps={placedAps}
            selectedApId={selectedApId}
            onAddApName={onAddApName}
            onAddAp={onAddAp}
            onSelectAp={id => {
              onSelectAp?.(id)
              onTabChange('inspect')
            }}
          />
        )}

        {tab === 'rf' && (
          <FloorPlanRfPanel
            placedApCount={placedApCount}
            bandGHz={geometry.bandGHz}
            geometry={geometry}
            metrics={
              rfMetrics ?? {
                weakPct: 0,
                fairPct: 0,
                goodPct: 0,
                highInterfPct: 0,
                severeInterfPct: 0,
                avgRssi: -95,
                score: 0,
              }
            }
            optimizationResult={optimizationResult ?? null}
            recommendations={recommendations}
            optimizing={!!optimizing || !!optimizationOverlayOpen}
            optimizationOverlayOpen={!!optimizationOverlayOpen}
            applyingRecommendationId={applyingRecommendationId ?? null}
            floorId={floorId}
            onGeometryPatch={onGeometryPatch}
            onOptimize={onOptimizeFloor ?? (() => {})}
            onApplyRecommendation={onApplyRecommendation ?? (() => {})}
            onDismissOptimization={onDismissOptimization}
            requirementPass={requirementPass ?? null}
            interferencePlan={interferencePlan ?? null}
          />
        )}

        {tab === 'inspect' && (
          <>
            {selectedClient && (
              <FloorPlanClientPanel
                client={selectedClient}
                onRunSpeedTestApi={onRunClientSpeedTest}
                onSpeedTestComplete={onClientSpeedTestComplete}
              />
            )}
            {selectedAp && !selectedClient && (
              <div className="space-y-2 rounded-lg border border-mg-accent/25 bg-mg-accent-soft/30 p-3">
                <p className="text-xs font-semibold text-mg-text">Access point · {selectedAp.ap}</p>
                <label className="block text-xs text-mg-muted">
                  TX power · {selectedAp.txPowerDbm ?? 15} dBm
                  <input
                    type="range"
                    min={5}
                    max={23}
                    step={1}
                    value={selectedAp.txPowerDbm ?? 15}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onTxPower(Number(e.target.value))}
                  />
                </label>
                <p className="text-xs text-mg-muted">
                  Channel · {formatChannelLabel(selectedAp.channel, geometry.bandGHz)}
                </p>
                <label className="block text-xs text-mg-muted">
                  Mount type
                  <Select
                    className="mt-1 w-full"
                    value={selectedAp.mount ?? 'ceiling'}
                    onChange={e => {
                      const mount = e.target.value as ApMountType
                      onApMount({
                        mount,
                        mountHeightM: defaultMountHeight(mount),
                        mountOrientationDeg: mount === 'wall' ? selectedAp.mountOrientationDeg ?? 0 : undefined,
                      })
                    }}
                  >
                    {AP_MOUNT_PRESETS.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                </label>
                <label className="block text-xs text-mg-muted">
                  Height · {selectedAp.mountHeightM ?? defaultMountHeight(selectedAp.mount)} m
                  <input
                    type="range"
                    min={1}
                    max={6}
                    step={0.1}
                    value={selectedAp.mountHeightM ?? defaultMountHeight(selectedAp.mount)}
                    className="floor-tx-slider mt-1 w-full"
                    onChange={e => onApMount({ mountHeightM: Number(e.target.value) })}
                  />
                </label>
                {(selectedAp.mount ?? 'ceiling') === 'wall' && (
                  <label className="block text-xs text-mg-muted">
                    Direction · {selectedAp.mountOrientationDeg ?? 0}°
                    <input
                      type="range"
                      min={0}
                      max={359}
                      step={1}
                      value={selectedAp.mountOrientationDeg ?? 0}
                      className="floor-tx-slider mt-1 w-full"
                      onChange={e => onApMount({ mountOrientationDeg: Number(e.target.value) })}
                    />
                  </label>
                )}
                {connectedClients.length > 0 && (
                  <div className="space-y-1.5 border-t border-mg-border/60 pt-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-mg-muted">
                      Connected clients · {connectedClients.length}
                    </p>
                    <ul className="max-h-36 space-y-1 overflow-y-auto">
                      {connectedClients.map(c => {
                        const rssiClass =
                          c.rssi >= -65 ? 'text-emerald-600' : c.rssi >= -75 ? 'text-amber-600' : 'text-red-600'
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => onSelectClient?.(c.id)}
                              className="w-full rounded-md border border-mg-border bg-white/60 px-2 py-1.5 text-left text-[11px] transition-colors hover:border-mg-accent/40"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate font-medium text-mg-text">{c.label ?? c.id}</span>
                                <span className={`shrink-0 font-semibold tabular-nums ${rssiClass}`}>
                                  {c.rssi} dBm
                                </span>
                              </div>
                              <p className="mt-0.5 truncate text-[10px] text-mg-muted">
                                {c.band ?? '5GHz'}
                                {c.ssid ? ` · ${c.ssid}` : ''}
                                {c.sticky ? ' · sticky' : ''}
                              </p>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
                <Button variant="ghost" size="sm" className="w-full text-mg-danger" onClick={onRemoveAp}>
                  <Trash2 size={14} /> Remove from floor
                </Button>
              </div>
            )}
            {!selectedAp &&
              !selectedClient &&
              !selectedDevice &&
              !selectedWall &&
              !selectedRoom &&
              !selectedObstacle &&
              !selectedAlarm && (
              <div className="rounded-lg border border-dashed border-mg-border px-3 py-6 text-center text-xs text-mg-muted">
                <Users size={20} className="mx-auto mb-2 opacity-50" />
                Select an AP or wireless client on the map · turn on the Clients layer to see RSSI · Delete with ⌫
              </div>
            )}
            {selectedWall && (
              <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
                <p className="text-sm font-semibold text-mg-text">Wall</p>
                <p className="text-xs capitalize text-mg-muted">{selectedWall.material.replace(/_/g, ' ')}</p>
                <p className="text-[11px] text-mg-muted">{formatWallSize(selectedWall)}</p>
                <label className="block text-xs text-mg-muted">
                  Replace material
                  <Select
                    value={selectedWall.material}
                    className="mt-1 w-full"
                    onChange={e => onReplaceWallMaterial(e.target.value as WallMaterial)}
                  >
                    {WALL_MATERIALS.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.label} · {m.attenuationDb} dB
                      </option>
                    ))}
                  </Select>
                </label>
                {(() => {
                  const dims = resolveWallDimensions(selectedWall)
                  return (
                    <>
                      <label className="block text-xs text-mg-muted">
                        Thickness · {dims.thicknessM.toFixed(2)} m
                        <input
                          type="range"
                          min={0.08}
                          max={0.5}
                          step={0.02}
                          value={dims.thicknessM}
                          className="floor-tx-slider mt-1 w-full"
                          onChange={e => onUpdateWallDims({ thicknessM: Number(e.target.value) })}
                        />
                      </label>
                      <label className="block text-xs text-mg-muted">
                        Height · {dims.heightM.toFixed(1)} m
                        <input
                          type="range"
                          min={0.8}
                          max={4}
                          step={0.1}
                          value={dims.heightM}
                          className="floor-tx-slider mt-1 w-full"
                          onChange={e => onUpdateWallDims({ heightM: Number(e.target.value) })}
                        />
                      </label>
                    </>
                  )
                })()}
                <GeometryClipActions
                  hint={geometryClipHint}
                  hasClipboard={hasGeometryClipboard}
                  onCopy={onCopyElement}
                  onDuplicate={onDuplicateElement}
                  onPaste={onPasteElement}
                />
                <Button variant="ghost" size="sm" className="w-full text-mg-danger" onClick={onDeleteWall}>
                  <Trash2 size={14} /> Delete wall
                </Button>
              </div>
            )}
            {selectedRoom && (
              <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
                <p className="text-sm font-semibold text-mg-text">Room</p>
                <label className="block text-xs text-mg-muted">
                  Name
                  <Input
                    value={selectedRoom.name}
                    className="mt-1"
                    onChange={e => onRenameRoom(e.target.value)}
                  />
                </label>
                <label className="flex items-center gap-2 text-xs text-mg-muted">
                  <input
                    type="checkbox"
                    checked={selectedRoom.coverageRequirement != null}
                    onChange={e =>
                      onUpdateRoomCoverage(
                        e.target.checked ? { minRssiDbm: selectedRoom.coverageRequirement?.minRssiDbm ?? -65 } : undefined,
                      )
                    }
                  />
                  Coverage requirement zone
                </label>
                {selectedRoom.coverageRequirement != null && (
                  <label className="block text-xs text-mg-muted">
                    Min RSSI (dBm)
                    <Input
                      type="number"
                      min={-85}
                      max={-55}
                      step={1}
                      className="mt-1"
                      value={selectedRoom.coverageRequirement.minRssiDbm}
                      onChange={e =>
                        onUpdateRoomCoverage({
                          minRssiDbm: Number(e.target.value) || -65,
                        })
                      }
                    />
                  </label>
                )}
                <GeometryClipActions
                  hint={geometryClipHint}
                  hasClipboard={hasGeometryClipboard}
                  onCopy={onCopyElement}
                  onDuplicate={onDuplicateElement}
                  onPaste={onPasteElement}
                />
                <Button variant="ghost" size="sm" className="w-full text-mg-danger" onClick={onDeleteRoom}>
                  <Trash2 size={14} /> Delete room
                </Button>
              </div>
            )}
            {selectedObstacle && (
              <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
                <p className="text-sm font-semibold text-mg-text">Obstacle</p>
                <p className="text-xs capitalize text-mg-muted">{selectedObstacle.type.replace(/_/g, ' ')}</p>
                <p className="text-[11px] text-mg-muted">
                  {formatObstacleSize(selectedObstacle, floorWidthM, floorHeightM)}
                </p>
                <label className="block text-xs text-mg-muted">
                  Replace type
                  <Select
                    value={selectedObstacle.type}
                    className="mt-1 w-full"
                    onChange={e => onReplaceObstacleType(e.target.value)}
                  >
                    {OBSTACLE_TYPES.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.label} · {o.attenuationDb} dB
                      </option>
                    ))}
                  </Select>
                </label>
                {(() => {
                  const dims = resolveObstacleFootprint(selectedObstacle, floorWidthM, floorHeightM)
                  return (
                    <>
                      <label className="block text-xs text-mg-muted">
                        Width · {dims.widthM.toFixed(1)} m
                        <input
                          type="range"
                          min={0.4}
                          max={4}
                          step={0.1}
                          value={dims.widthM}
                          className="floor-tx-slider mt-1 w-full"
                          onChange={e => onUpdateObstacleDims({ widthM: Number(e.target.value) })}
                        />
                      </label>
                      <label className="block text-xs text-mg-muted">
                        Depth · {dims.depthM.toFixed(1)} m
                        <input
                          type="range"
                          min={0.4}
                          max={4}
                          step={0.1}
                          value={dims.depthM}
                          className="floor-tx-slider mt-1 w-full"
                          onChange={e => onUpdateObstacleDims({ depthM: Number(e.target.value) })}
                        />
                      </label>
                      <label className="block text-xs text-mg-muted">
                        Height · {dims.heightM.toFixed(1)} m
                        <input
                          type="range"
                          min={0.8}
                          max={4}
                          step={0.1}
                          value={dims.heightM}
                          className="floor-tx-slider mt-1 w-full"
                          onChange={e => onUpdateObstacleDims({ heightM: Number(e.target.value) })}
                        />
                      </label>
                    </>
                  )
                })()}
                <GeometryClipActions
                  hint={geometryClipHint}
                  hasClipboard={hasGeometryClipboard}
                  onCopy={onCopyElement}
                  onDuplicate={onDuplicateElement}
                  onPaste={onPasteElement}
                />
                <Button variant="ghost" size="sm" className="w-full text-mg-danger" onClick={onDeleteObstacle}>
                  <Trash2 size={14} /> Delete obstacle
                </Button>
              </div>
            )}
            {selectedDevice && (
              <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
                <p className="text-sm font-semibold text-mg-text">{selectedDevice.name}</p>
                <p className="text-xs text-mg-muted capitalize">{selectedDevice.type.replace(/_/g, ' ')}</p>
                <GeometryClipActions
                  hint={geometryClipHint}
                  hasClipboard={hasGeometryClipboard}
                  onCopy={onCopyElement}
                  onDuplicate={onDuplicateElement}
                  onPaste={onPasteElement}
                />
                <Button variant="ghost" size="sm" className="w-full text-mg-danger" onClick={onDeleteDevice}>
                  <Trash2 size={14} /> Delete device
                </Button>
              </div>
            )}
            {selectedAlarm && layers.alarms && (
              <div className="space-y-2 rounded-lg border border-mg-danger-border bg-mg-danger-soft p-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-mg-danger">
                  <AlertTriangle size={15} /> {selectedAlarm.title}
                </p>
                <p className="text-xs text-mg-text">Device: {selectedAlarm.device}</p>
                <p className="text-xs text-mg-muted">Severity: {selectedAlarm.severity}</p>
                <Button variant="secondary" size="sm" className="w-full" onClick={onDismissAlarm}>
                  Dismiss
                </Button>
              </div>
            )}
            {showTopology && (selectedAp || selectedDevice) && (
              <div className="space-y-2 rounded-lg border border-mg-border bg-mg-page p-3">
                <p className="text-xs font-semibold text-mg-text">Topology trace</p>
                <ol className="space-y-1 text-[11px] text-mg-muted">
                  <li className="rounded bg-mg-surface px-2 py-1">
                    1. {selectedAp?.ap ?? selectedDevice?.name} (floor)
                  </li>
                  <li className="rounded bg-mg-surface px-2 py-1">2. IDF-SW-01 · Gi0/12</li>
                  <li className="rounded bg-mg-surface px-2 py-1">3. Patch panel PP-2 · port 14</li>
                  <li className="rounded bg-mg-surface px-2 py-1">4. MDF-CORE-01 · Te1/0/3</li>
                </ol>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  )
}
